import React, { useEffect, useState } from "react";
import { db } from "../firebase";

import {
  collection,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";

import { useNavigate } from "react-router-dom";

import {
  FaUsers,
  FaUser,
  FaCalendarAlt,
  FaFutbol,
  FaChevronDown,
  FaChevronUp,
  FaChevronRight,
  FaWallet,
  FaArrowLeft,
  FaSyncAlt,
  FaClock,
} from "react-icons/fa";

import "./Team.css";


/* =========================================================
   CACHE SETTINGS
========================================================= */

const CACHE_TIME = 2 * 60 * 1000;

const getCacheKey = (username) =>
  `myTeamCache_${username}`;


/* =========================================================
   TODAY DATE
   Uses local device date instead of UTC.
========================================================= */

const getTodayDate = () => {

  const today = new Date();

  const year =
    today.getFullYear();

  const month =
    String(
      today.getMonth() + 1
    ).padStart(2, "0");

  const day =
    String(
      today.getDate()
    ).padStart(2, "0");

  return `${year}-${month}-${day}`;
};


/* =========================================================
   FORMAT NUMBER
========================================================= */

const formatNumber = (value) => {

  const number =
    Number(value || 0);

  if (Number.isNaN(number)) {
    return "0";
  }

  return number.toLocaleString();
};


/* =========================================================
   FORMAT DATE
========================================================= */

const formatDate = (timestamp) => {

  if (!timestamp) {
    return "N/A";
  }

  try {

    if (
      timestamp?.seconds
    ) {

      return new Date(
        timestamp.seconds * 1000
      ).toLocaleDateString();

    }

    if (
      timestamp?.toDate
    ) {

      return timestamp
        .toDate()
        .toLocaleDateString();

    }

    const date =
      new Date(timestamp);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {

      return "N/A";

    }

    return date.toLocaleDateString();

  } catch {

    return "N/A";

  }

};


/* =========================================================
   FORMAT TIME
========================================================= */

const formatTime = (timestamp) => {

  if (!timestamp) {
    return "--:--";
  }

  try {

    let date;

    if (
      timestamp?.seconds
    ) {

      date =
        new Date(
          timestamp.seconds * 1000
        );

    } else if (
      timestamp?.toDate
    ) {

      date =
        timestamp.toDate();

    } else {

      date =
        new Date(timestamp);

    }

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {

      return "--:--";

    }

    return date.toLocaleTimeString(
      [],
      {
        hour: "2-digit",
        minute: "2-digit",
      }
    );

  } catch {

    return "--:--";

  }

};


/* =========================================================
   RESULT CLASS
========================================================= */

const getResultClass = (result) => {

  const value =
    String(
      result || ""
    )
      .trim()
      .toLowerCase();

  if (
    value === "lose" ||
    value === "loss" ||
    value === "lost"
  ) {

    return "bet-result lose";

  }

  if (
    value === "win" ||
    value === "won"
  ) {

    return "bet-result win";

  }

  return "bet-result other";

};


/* =========================================================
   NORMALIZE BET
========================================================= */

const normalizeBet = (
  doc,
  username
) => {

  const data =
    doc.data();


  const amount =
    Number(
      data.stake ??
      data.amount ??
      data.quantity ??
      0
    );


  return {

    id:
      doc.id,

    username:
      data.username ||
      username,

    amount,

    stake:
      Number(
        data.stake ??
        data.amount ??
        data.quantity ??
        0
      ),

    currency:
      data.currency ||
      "TZS",

    betId:
      data.betId ||
      "",

    createdAt:
      data.createdAt ||
      null,

    matchDate:
      data.matchDate ||
      "",

    matchDateText:
      data.matchDateText ||
      "",

    matchTime:
      data.matchTime ||
      "",

    matchTimestamp:
      data.matchTimestamp ||
      null,

    league:
      data.league ||
      "",

    teamA:
      data.teamA ||
      "",

    teamB:
      data.teamB ||
      "",

    score:
      data.score ||
      "",

    odds:
      data.odds ??
      0,

    result:
      data.result ||
      "",

    profit:
      Number(
        data.profit ||
        0
      ),

    totalWin:
      Number(
        data.totalWin ||
        0
      ),

    type:
      data.type ||
      "antscore",

  };

};


/* =========================================================
   MY TEAM
========================================================= */

const MyTeam = () => {

  const navigate =
    useNavigate();


  /* =======================================================
     STATE
  ======================================================= */

  const [levels, setLevels] =
    useState({
      1: [],
      2: [],
      3: [],
    });


  const [activeLevel, setActiveLevel] =
    useState(1);


  const [expandedUserId, setExpandedUserId] =
    useState(null);


  const [expandedBetId, setExpandedBetId] =
    useState(null);


  const [loading, setLoading] =
    useState(true);


  const [refreshing, setRefreshing] =
    useState(false);


  const loggedInUsername =
    localStorage.getItem(
      "username"
    );


  /* =======================================================
     SAVE CACHE
  ======================================================= */

  const saveTeamCache =
    (data) => {

      if (
        !loggedInUsername
      ) {
        return;
      }

      try {

        localStorage.setItem(
          getCacheKey(
            loggedInUsername
          ),
          JSON.stringify({
            data,
            updatedAt:
              Date.now(),
          })
        );

      } catch (error) {

        console.error(
          "Failed saving team cache:",
          error
        );

      }

    };


  /* =======================================================
     LOAD CACHE
  ======================================================= */

  const loadTeamCache =
    () => {

      if (
        !loggedInUsername
      ) {
        return null;
      }

      try {

        const raw =
          localStorage.getItem(
            getCacheKey(
              loggedInUsername
            )
          );


        if (!raw) {
          return null;
        }


        const cached =
          JSON.parse(raw);


        if (
          !cached?.data
        ) {
          return null;
        }


        return cached;

      } catch (error) {

        console.error(
          "Failed loading team cache:",
          error
        );

        return null;

      }

    };


  /* =======================================================
     CURRENT USER
  ======================================================= */

  const fetchCurrentUser =
    async () => {

      if (
        !loggedInUsername
      ) {
        return null;
      }


      const q =
        query(
          collection(
            db,
            "users"
          ),
          where(
            "username",
            "==",
            loggedInUsername
          ),
          limit(1)
        );


      const snap =
        await getDocs(q);


      if (
        snap.empty
      ) {
        return null;
      }


      return {

        id:
          snap.docs[0].id,

        ...snap.docs[0].data(),

      };

    };


  /* =======================================================
     FIRST TRANSACTION
  ======================================================= */

  const fetchFirstTransaction =
    async (username) => {

      const collections = [
        "tsh_transactions",
        "usdt_transactions",
      ];


      for (
        const collectionName
        of collections
      ) {

        try {

          const q =
            query(
              collection(
                db,
                collectionName
              ),
              where(
                "username",
                "==",
                username
              ),
              where(
                "position",
                "==",
                "first"
              ),
              where(
                "status",
                "==",
                "success"
              ),
              limit(1)
            );


          const snap =
            await getDocs(q);


          if (
            !snap.empty
          ) {

            return {

              ...snap.docs[0].data(),

              type:
                collectionName,

            };

          }

        } catch (error) {

          console.error(
            `Transaction error ${collectionName}:`,
            error
          );

        }

      }


      return null;

    };


  /* =======================================================
     FETCH TODAY BETS FROM ANTSCORE
  ======================================================= */

  const fetchTodayBets =
    async (username) => {

      if (!username) {
        return [];
      }


      const today =
        getTodayDate();


      try {

        const q =
          query(
            collection(
              db,
              "antscore"
            ),
            where(
              "username",
              "==",
              username
            ),
            where(
              "matchDate",
              "==",
              today
            )
          );


        const snap =
          await getDocs(q);


        const bets = [];


        snap.forEach(
          (doc) => {

            bets.push(
              normalizeBet(
                doc,
                username
              )
            );

          }
        );


        /*
         * Latest bet first.
         */

        bets.sort(
          (a, b) => {

            const dateA =
              a.createdAt?.seconds
                ? a.createdAt.seconds * 1000
                : new Date(
                    a.createdAt || 0
                  ).getTime();


            const dateB =
              b.createdAt?.seconds
                ? b.createdAt.seconds * 1000
                : new Date(
                    b.createdAt || 0
                  ).getTime();


            return dateB - dateA;

          }
        );


        return bets;

      } catch (error) {

        console.error(
          "Failed fetching antscore bets:",
          error
        );

        return [];

      }

    };


  /* =======================================================
     FETCH REFERRALS BY LEVEL
  ======================================================= */

  const fetchReferralsByLevel =
    async (
      codes,
      maxLevel = 3
    ) => {

      const data = {
        1: [],
        2: [],
        3: [],
      };


      let level = 1;

      let currentCodes =
        codes;


      while (
        level <= maxLevel &&
        currentCodes.length > 0
      ) {

        /*
         * Firestore "in"
         * maximum 10 values.
         */

        const chunks = [];


        for (
          let i = 0;
          i < currentCodes.length;
          i += 10
        ) {

          chunks.push(
            currentCodes.slice(
              i,
              i + 10
            )
          );

        }


        let users = [];


        const snaps =
          await Promise.all(
            chunks.map(
              (chunk) =>
                getDocs(
                  query(
                    collection(
                      db,
                      "users"
                    ),
                    where(
                      "whoReferredWho",
                      "in",
                      chunk
                    )
                  )
                )
            )
          );


        snaps.forEach(
          (snap) => {

            snap.forEach(
              (doc) => {

                users.push({

                  id:
                    doc.id,

                  ...doc.data(),

                });

              }
            );

          }
        );


        /*
         * Remove duplicate users.
         */

        users =
          Array.from(
            new Map(
              users.map(
                (user) => [
                  user.id,
                  user,
                ]
              )
            ).values()
          );


        /*
         * Fetch all extra data
         * in parallel.
         */

        const usersWithExtras =
          await Promise.all(
            users.map(
              async (user) => {

                const [
                  firstTx,
                  todayBets,
                ] =
                  await Promise.all([
                    fetchFirstTransaction(
                      user.username
                    ),

                    fetchTodayBets(
                      user.username
                    ),
                  ]);


                /*
                 * Calculate today's
                 * total stake.
                 */

                const todayBetTotal =
                  todayBets.reduce(
                    (
                      total,
                      bet
                    ) =>
                      total +
                      Number(
                        bet.amount ||
                        0
                      ),
                    0
                  );


                /*
                 * Currency.
                 */

                const todayBetCurrency =
                  todayBets[0]
                    ?.currency ||
                  "";


                return {

                  ...user,

                  firstTx,

                  todayBets,

                  todayBet: {

                    totalBet:
                      todayBetTotal,

                    currency:
                      todayBetCurrency,

                  },

                };

              }
            )
          );


        data[level] =
          usersWithExtras;


        /*
         * Continue to next level
         * using referral codes.
         */

        currentCodes =
          usersWithExtras
            .map(
              (user) =>
                user.referralCode
            )
            .filter(Boolean);


        level++;

      }


      setLevels(data);

      saveTeamCache(data);


      return data;

    };


  /* =======================================================
     LOAD TEAM
  ======================================================= */

  const loadTeam =
    async (
      showLoader = true
    ) => {

      if (
        !loggedInUsername
      ) {

        setLoading(false);

        return;

      }


      if (
        showLoader
      ) {

        setRefreshing(true);

      }


      try {

        const user =
          await fetchCurrentUser();


        if (
          user?.referralCode
        ) {

          await fetchReferralsByLevel(
            [
              user.referralCode,
            ],
            3
          );

        } else {

          setLevels({
            1: [],
            2: [],
            3: [],
          });

        }

      } catch (error) {

        console.error(
          "Failed loading team:",
          error
        );

      } finally {

        setLoading(false);

        setRefreshing(false);

      }

    };


  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {

    if (
      !loggedInUsername
    ) {

      setLoading(false);

      return;

    }


    /*
     * Show cached data
     * immediately.
     */

    const cached =
      loadTeamCache();


    if (
      cached?.data
    ) {

      setLevels({

        1:
          cached.data[1] ||
          [],

        2:
          cached.data[2] ||
          [],

        3:
          cached.data[3] ||
          [],

      });

      setLoading(false);

    }


    /*
     * Background refresh.
     */

    loadTeam(false);


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loggedInUsername
  ]);


  /* =======================================================
     AUTO REFRESH
  ======================================================= */

  useEffect(() => {

    if (
      !loggedInUsername
    ) {
      return;
    }


    const interval =
      setInterval(
        () => {

          loadTeam(false);

        },
        CACHE_TIME
      );


    return () => {

      clearInterval(
        interval
      );

    };


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loggedInUsername
  ]);


  /* =======================================================
     TOGGLE USER
  ======================================================= */

  const toggleUser =
    (id) => {

      setExpandedUserId(
        (previous) =>
          previous === id
            ? null
            : id
      );


      /*
       * Close any open bet
       * when switching user.
       */

      setExpandedBetId(
        null
      );

    };


  /* =======================================================
     TOGGLE BET
  ======================================================= */

  const toggleBet =
    (id) => {

      setExpandedBetId(
        (previous) =>
          previous === id
            ? null
            : id
      );

    };


  /* =======================================================
     CURRENT USERS
  ======================================================= */

  const currentUsers =
    levels[activeLevel] ||
    [];


  /* =======================================================
     TOTAL MEMBERS
  ======================================================= */

  const totalMembers =
    (levels[1]?.length || 0) +
    (levels[2]?.length || 0) +
    (levels[3]?.length || 0);


  /* =======================================================
     TOTAL TODAY BET
  ======================================================= */

  const totalTodayBet =
    Object.values(
      levels
    )
      .flat()
      .reduce(
        (
          total,
          user
        ) =>
          total +
          Number(
            user.todayBet
              ?.totalBet ||
            0
          ),
        0
      );


  /* =======================================================
     LOADING
  ======================================================= */

  if (
    loading
  ) {

    return (

      <div className="team-wrapper">

        <div className="team-loading">

          <div className="loading-spinner" />

          <span>
            Loading your team...
          </span>

        </div>

      </div>

    );

  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (

    <div className="team-wrapper">


      {/* =================================================
          TOP HEADER
      ================================================= */}

      <div className="team-topbar">


        <button
          type="button"
          className="team-back-btn"
          onClick={() =>
            navigate(-1)
          }
        >

          <FaArrowLeft />

          <span>
            Back
          </span>

        </button>


        <div className="team-page-title">

          <span>
            AGENCY
          </span>

          <h1>
            My Team
          </h1>

        </div>


        <button
          type="button"
          className={
            refreshing
              ? "team-refresh-btn spinning"
              : "team-refresh-btn"
          }
          onClick={() =>
            loadTeam(true)
          }
          title="Refresh team"
        >

          <FaSyncAlt />

        </button>

      </div>


      {/* =================================================
          TEAM SUMMARY
      ================================================= */}

      <div className="team-summary">

        <div className="team-summary-icon">

          <FaUsers />

        </div>


        <div>

          <span>
            TOTAL MEMBERS
          </span>

          <strong>
            {totalMembers}
          </strong>

        </div>


        <div className="summary-level">

          <span>
            TODAY BET
          </span>

          <strong>
            {formatNumber(
              totalTodayBet
            )}
          </strong>

        </div>

      </div>


      {/* =================================================
          LEVEL TABS
      ================================================= */}

      <div className="level-tabs">

        {[1, 2, 3].map(
          (level) => (

            <button
              key={level}
              type="button"
              className={
                activeLevel === level
                  ? "level-tab active"
                  : "level-tab"
              }
              onClick={() => {

                setActiveLevel(
                  level
                );

                setExpandedUserId(
                  null
                );

                setExpandedBetId(
                  null
                );

              }}
            >

              <span>
                Level {level}
              </span>

              <strong>
                {
                  levels[level]
                    ?.length || 0
                }
              </strong>

            </button>

          )
        )}

      </div>


      {/* =================================================
          LEVEL INFO
      ================================================= */}

      <div className="level-info">

        <div>

          <span>
            TEAM NETWORK
          </span>

          <strong>
            Level {activeLevel}
          </strong>

        </div>


        <div className="level-member-count">

          {
            currentUsers.length
          }{" "}

          members

        </div>

      </div>


      {/* =================================================
          EMPTY
      ================================================= */}

      {currentUsers.length === 0 ? (

        <div className="empty-team">

          <div className="empty-icon">

            <FaUsers />

          </div>


          <h3>
            No members yet
          </h3>


          <p>
            There are no members
            in Level {activeLevel}.
          </p>

        </div>

      ) : (


        /* =================================================
           USER LIST
        ================================================= */

        <div className="user-list">

          {currentUsers.map(
            (
              user,
              index
            ) => {

              const isExpanded =
                expandedUserId ===
                user.id;


              const todayBet =
                user.todayBet || {
                  totalBet: 0,
                  currency: "",
                };


              const todayBets =
                user.todayBets ||
                [];


              return (

                <div
                  key={
                    user.id
                  }
                  className={
                    isExpanded
                      ? "team-row expanded"
                      : "team-row"
                  }
                >


                  {/* =====================================
                      MAIN USER ROW
                  ===================================== */}

                  <button
                    type="button"
                    className="row-main"
                    onClick={() =>
                      toggleUser(
                        user.id
                      )
                    }
                  >


                    <div className="user-left">

                      <div className="user-number">

                        {
                          index + 1
                        }

                      </div>


                      <div className="user-avatar">

                        <FaUser />

                      </div>


                      <div className="user-name-box">

                        <strong>

                          {
                            user.username ||
                            "Unknown"
                          }

                        </strong>


                        <span>

                          Level{" "}
                          {
                            activeLevel
                          }

                        </span>

                      </div>

                    </div>


                    {/* BALANCE */}

                    <div className="user-balance">

                      <span>
                        BALANCE
                      </span>


                      <strong>

                        {formatNumber(
                          user.tshBalance ||
                          0
                        )}

                      </strong>

                    </div>


                    {/* ARROW */}

                    <div className="expand-icon">

                      {
                        isExpanded
                          ? (
                            <FaChevronUp />
                          )
                          : (
                            <FaChevronDown />
                          )
                      }

                    </div>

                  </button>


                  {/* =====================================
                      USER DETAILS
                  ===================================== */}

                  {isExpanded && (

                    <div className="row-details">


                      {/* JOINED */}

                      <div className="detail-item">

                        <FaCalendarAlt />

                        <div>

                          <span>
                            Joined
                          </span>

                          <strong>

                            {
                              formatDate(
                                user.createdAt
                              )
                            }

                          </strong>

                        </div>

                      </div>


                      {/* TODAY BET */}

                      <div className="detail-item">

                        <FaFutbol />

                        <div>

                          <span>
                            Today's Bet
                          </span>

                          <strong>

                            {
                              todayBet.totalBet >
                              0
                                ? `${formatNumber(
                                    todayBet.totalBet
                                  )} ${
                                    todayBet.currency ||
                                    "TZS"
                                  }`
                                : "0"
                            }

                          </strong>

                        </div>

                      </div>


                      {/* BALANCE */}

                      <div className="detail-item">

                        <FaWallet />

                        <div>

                          <span>
                            Balance
                          </span>

                          <strong>

                            TZS{" "}

                            {
                              formatNumber(
                                user.tshBalance ||
                                0
                              )
                            }

                          </strong>

                        </div>

                      </div>


                      {/* =================================
                          TODAY BET LIST
                      ================================= */}

                      <div className="today-bets-section">


                        <div className="today-bets-header">

                          <div>

                            <span>
                              TODAY BETS
                            </span>

                            <strong>
                              {
                                todayBets.length
                              }{" "}
                              bets
                            </strong>

                          </div>


                          <div className="today-bets-total">

                            {
                              formatNumber(
                                todayBet.totalBet
                              )
                            }{" "}

                            {
                              todayBet.currency ||
                              "TZS"
                            }

                          </div>

                        </div>


                        {todayBets.length === 0 ? (

                          <div className="today-bets-empty">

                            <FaFutbol />

                            <span>
                              No bets today
                            </span>

                          </div>

                        ) : (

                          <div className="today-bets-list">

                            {todayBets.map(
                              (
                                bet,
                                betIndex
                              ) => {

                                const isBetExpanded =
                                  expandedBetId ===
                                  bet.id;


                                return (

                                  <div
                                    key={
                                      bet.id
                                    }
                                    className={
                                      isBetExpanded
                                        ? "today-bet-card expanded"
                                        : "today-bet-card"
                                    }
                                  >


                                    {/* =================================
                                        BET MAIN ROW
                                    ================================= */}

                                    <button
                                      type="button"
                                      className="today-bet-row"
                                      onClick={() =>
                                        toggleBet(
                                          bet.id
                                        )
                                      }
                                    >


                                      <div className="today-bet-number">

                                        {
                                          String(
                                            betIndex + 1
                                          ).padStart(
                                            2,
                                            "0"
                                          )
                                        }

                                      </div>


                                      <div className="today-bet-icon">

                                        <FaFutbol />

                                      </div>


                                      <div className="today-bet-user">

                                        <strong>

                                          {
                                            user.username ||
                                            "Unknown"
                                          }

                                        </strong>


                                        <span
                                          className={
                                            String(
                                              bet.result ||
                                              ""
                                            )
                                              .toLowerCase() ===
                                            "lose"
                                              ? "today-bet-amount lose"
                                              : "today-bet-amount"
                                          }
                                        >

                                          {
                                            formatNumber(
                                              bet.amount
                                            )
                                          }{" "}

                                          {
                                            bet.currency
                                          }

                                        </span>

                                      </div>


                                      <div className="today-bet-time">

                                        <FaClock />

                                        {
                                          formatTime(
                                            bet.createdAt
                                          )
                                        }

                                      </div>


                                      {/* ONLY ARROW */}

                                      <div className="today-bet-arrow">

                                        {
                                          isBetExpanded
                                            ? (
                                              <FaChevronUp />
                                            )
                                            : (
                                              <FaChevronRight />
                                            )
                                        }

                                      </div>

                                    </button>


                                    {/* =================================
                                        BET DETAILS
                                    ================================= */}

                                    {isBetExpanded && (

                                      <div className="today-bet-details">


                                        {/* BET ID */}

                                        <div className="bet-detail">

                                          <span>
                                            BET ID
                                          </span>

                                          <strong>
                                            {
                                              bet.betId ||
                                              "-"
                                            }
                                          </strong>

                                        </div>


                                        {/* AMOUNT */}

                                        <div className="bet-detail">

                                          <span>
                                            STAKE
                                          </span>

                                          <strong
                                            className={
                                              String(
                                                bet.result ||
                                                ""
                                              )
                                                .toLowerCase() ===
                                              "lose"
                                                ? "bet-red"
                                                : "bet-green"
                                            }
                                          >

                                            {
                                              formatNumber(
                                                bet.stake
                                              )
                                            }{" "}

                                            {
                                              bet.currency
                                            }

                                          </strong>

                                        </div>


                                        {/* RESULT */}

                                        <div className="bet-detail">

                                          <span>
                                            RESULT
                                          </span>

                                          <strong
                                            className={
                                              getResultClass(
                                                bet.result
                                              )
                                            }
                                          >

                                            {
                                              bet.result
                                                ? String(
                                                    bet.result
                                                  ).toUpperCase()
                                                : "-"
                                            }

                                          </strong>

                                        </div>


                                        {/* MATCH */}

                                        <div className="bet-detail bet-detail-full">

                                          <span>
                                            MATCH
                                          </span>

                                          <strong>
                                            {
                                              bet.teamA ||
                                              "-"
                                            }

                                            {" "}vs{" "}

                                            {
                                              bet.teamB ||
                                              "-"
                                            }

                                          </strong>

                                        </div>


                                        {/* LEAGUE */}

                                        <div className="bet-detail">

                                          <span>
                                            LEAGUE
                                          </span>

                                          <strong>
                                            {
                                              bet.league ||
                                              "-"
                                            }
                                          </strong>

                                        </div>


                                        {/* ODDS */}

                                        <div className="bet-detail">

                                          <span>
                                            ODDS
                                          </span>

                                          <strong>
                                            {
                                              bet.odds ||
                                              "-"
                                            }
                                          </strong>

                                        </div>


                                        {/* SCORE */}

                                        <div className="bet-detail">

                                          <span>
                                            SCORE
                                          </span>

                                          <strong>
                                            {
                                              bet.score ||
                                              "-"
                                            }
                                          </strong>

                                        </div>


                                        {/* MATCH TIME */}

                                        <div className="bet-detail">

                                          <span>
                                            MATCH TIME
                                          </span>

                                          <strong>
                                            {
                                              bet.matchTime ||
                                              "-"
                                            }
                                          </strong>

                                        </div>


                                        {/* TOTAL WIN */}

                                        <div className="bet-detail">

                                          <span>
                                            TOTAL WIN
                                          </span>

                                          <strong
                                            className={
                                              String(
                                                bet.result ||
                                                ""
                                              )
                                                .toLowerCase() ===
                                              "lose"
                                                ? "bet-red"
                                                : "bet-green"
                                            }
                                          >

                                            {
                                              formatNumber(
                                                bet.totalWin
                                              )
                                            }{" "}

                                            {
                                              bet.currency
                                            }

                                          </strong>

                                        </div>


                                        {/* PROFIT */}

                                        <div className="bet-detail">

                                          <span>
                                            PROFIT
                                          </span>

                                          <strong
                                            className={
                                              Number(
                                                bet.profit ||
                                                0
                                              ) < 0
                                                ? "bet-red"
                                                : "bet-green"
                                            }
                                          >

                                            {
                                              formatNumber(
                                                bet.profit
                                              )
                                            }{" "}

                                            {
                                              bet.currency
                                            }

                                          </strong>

                                        </div>


                                        {/* BET DATE */}

                                        <div className="bet-detail">

                                          <span>
                                            DATE
                                          </span>

                                          <strong>
                                            {
                                              formatDate(
                                                bet.createdAt
                                              )
                                            }
                                          </strong>

                                        </div>


                                      </div>

                                    )}

                                  </div>

                                );

                              }
                            )}

                          </div>

                        )}

                      </div>

                    </div>

                  )}

                </div>

              );

            }
          )}

        </div>

      )}

    </div>

  );

};


export default MyTeam;

